import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { fontsize10, poppins400, poppins500 } from 'theme/common.style';
import { postApiCall } from 'api/apiService';
import { SHARE_WINNER } from 'constant/app.config';
import Swal from 'sweetalert2';
import Loading from 'assets/images/loading.png';

const Warning = () => {
  return (
    <svg
      width="38"
      height="36"
      viewBox="0 0 38 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        width="37.5797"
        height="36"
        rx="18"
        fill="#10D35E"
        fill-opacity="0.1"
      />
      <path
        d="M27.0817 21.4607L21.3085 11.456C20.7883 10.5551 19.8271 10 18.7866 10C17.7462 10 16.7851 10.5551 16.2648 11.456L10.3902 21.632C9.86994 22.5329 9.86994 23.6431 10.3902 24.544C10.9104 25.4449 11.8715 26 12.912 26H24.6614C24.6646 26 24.6677 26 24.6677 26C26.276 26 27.5797 24.6962 27.5797 23.088C27.5797 22.4853 27.3957 21.9239 27.0817 21.4607ZM20.0333 22.4345C20.0333 23.1229 19.5036 23.6495 18.7898 23.6495C18.0761 23.6495 17.5464 23.1229 17.5464 22.4345V22.406C17.5464 21.7208 18.0761 21.1911 18.7898 21.1911C19.5036 21.1911 20.0333 21.7177 20.0333 22.406V22.4345ZM20.0619 14.2443L19.4465 19.6178C19.4052 20.0048 19.1483 20.2458 18.7898 20.2458C18.4314 20.2458 18.1745 20.0016 18.1332 19.6178L17.5178 14.2411C17.4766 13.8255 17.7019 13.5115 18.0888 13.5115H19.4877C19.8747 13.5147 20.1031 13.8287 20.0619 14.2443Z"
        fill="#10D35E"
      />
    </svg>
  );
};

const ShareWinnerDialog = ({
  open,
  onClose,
  tableData,
  propAddress,
  profileDetails,
  onSubmit,
}) => {
  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedBuyers, setSelectedBuyers] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ------------------ INITIALIZE SELECTED BUYERS ------------------ */
  useEffect(() => {
    if (open && tableData?.PropertyDetails?.winnerSharedTo) {
      // Start with already shared buyers pre-selected
      const alreadyShared = tableData.PropertyDetails.winnerSharedTo;
      setSelectedBuyers(alreadyShared);
    }
  }, [open, tableData]);

  const handleShare = () => {
    setLoading(true);
    const inputData = {
      buyerArr: selectedBuyers,
      offerUniqueId: tableData?.PropertyDetails?.winnerOfferUniqueId,
    };
    postApiCall(SHARE_WINNER, inputData)
      .then((res) => {
        if (res?.data?.status == '1') {
          setLoading(false);
          onClose();

          Swal.fire({
            title: res?.data?.heading
              ? res?.data?.heading
              : 'Winner Details Shared',
            text: res?.data?.message,
            showDenyButton: false,
            showCancelButton: false,
            icon: 'success',
            confirmButtonText: 'Close',
          }).then((result) => {
            if (result.isConfirmed) {
            } else if (result.isDenied) {
              return;
            }
          });
        } else {
          setLoading(false);
          onClose();
          Swal.fire({
            title: res?.data?.heading ? res?.data?.heading : 'Error',
            text: res?.data?.message,
            showDenyButton: false,
            showCancelButton: false,
            icon: 'error',
            confirmButtonText: 'Close',
          }).then((result) => {
            if (result.isConfirmed) {
            } else if (result.isDenied) {
              return;
            }
          });
        }
      })
      .catch(() => {
        Swal.fire({
          title: 'Error',
          text: 'Try again later',
          showDenyButton: false,
          showCancelButton: false,
          icon: 'error',
          confirmButtonText: 'Close',
        }).then((result) => {
          if (result.isConfirmed) {
          } else if (result.isDenied) {
            return;
          }
        });
      });
  };

  const getEligibleBuyerIds = () => {
    return (
      tableData?.BuyerRegisterDetails?.filter(
        (buyer) =>
          buyer?.buyerOfferDetails?.isWinner !== 1 &&
          !tableData?.PropertyDetails?.winnerSharedTo?.includes(
            buyer?.buyerDetails?.user?.userId
          )
      ).map((buyer) => buyer?.buyerDetails?.user?.userId) || []
    );
  };

  const handleSelectAllEligible = (e) => {
    const eligibleBuyerIds = getEligibleBuyerIds();

    if (e.target.checked) {
      setSelectedBuyers(eligibleBuyerIds);
    } else {
      setSelectedBuyers([]);
    }
  };

  const eligibleBuyerIds = getEligibleBuyerIds();

  const newlySelectedEligibleBuyers = selectedBuyers.filter((id) =>
    eligibleBuyerIds.includes(id)
  );

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          borderRadius: '5px',
          minWidth: matchessm ? '95%' : '900px',
          boxShadow: '-4px 4px rgba(0, 0, 0, 0.09)',
          padding: matchessm ? '25px 15px' : '20px',
        },
      }}
    >
      <Grid container height="100%">
        {/* Address */}
        <Grid item xs={12}>
          <Typography
            sx={{
              ...poppins400,
              fontSize: matchessm ? '10px' : '14px',
              color: '#666666',
            }}
          >
            {propAddress}&nbsp;
          </Typography>
        </Grid>

        {/* Header */}
        <Grid
          item
          xs={12}
          display="flex"
          alignItems="center"
          padding="12px 0px 15px 0"
        >
          <Warning />
          <Typography
            sx={{
              ...poppins500,
              fontSize: matchessm ? '16px' : '24px',
              paddingLeft: '12px',
            }}
          >
            Share Winner
          </Typography>
        </Grid>

        {/* Info text */}
        <Grid item xs={12} paddingBottom="10px">
          <Typography
            sx={{
              ...poppins400,
              fontSize: matchessm ? '12px' : '16px',
              color: '#666666',
            }}
          >
            Select at least <b>a buyer</b> to share winning offer price.
          </Typography>
        </Grid>

        {/* TABLE */}
        <Grid item xs={12}>
          <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: '50px',
                      textAlign: 'center',
                      padding: '8px',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        getEligibleBuyerIds().length > 0 &&
                        getEligibleBuyerIds().every((id) =>
                          selectedBuyers.includes(id)
                        )
                      }
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            selectedBuyers.length > 0 &&
                            selectedBuyers.some((id) =>
                              getEligibleBuyerIds().includes(id)
                            ) &&
                            !getEligibleBuyerIds().every((id) =>
                              selectedBuyers.includes(id)
                            );
                        }
                      }}
                      onChange={handleSelectAllEligible}
                      disabled={getEligibleBuyerIds().length === 0}
                      style={{
                        accentColor: '#A55FFB',
                        width: '16px',
                        height: '16px',
                        cursor:
                          getEligibleBuyerIds().length === 0
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                    />
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: '10px',
                      fontWeight: '400',
                      fontFamily: 'Poppins',
                      color: '#112347',
                      textAlign: 'left',
                      padding: '8px',
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '10px',
                      fontWeight: '400',
                      fontFamily: 'Poppins',
                      color: '#112347',
                      textAlign: 'center',
                      padding: '8px',
                    }}
                  >
                    Offer
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '10px',
                      fontWeight: '400',
                      fontFamily: 'Poppins',
                      color: '#112347',
                      textAlign: 'center',
                      padding: '8px',
                    }}
                  >
                    No. of Offers
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData?.BuyerRegisterDetails?.length ? (
                  tableData.BuyerRegisterDetails
                    // Filter out the winner (isWinner === 1)
                    .filter((buyer) => buyer?.buyerOfferDetails?.isWinner !== 1)
                    .map((buyer, index) => {
                      const buyerId = buyer?.buyerDetails?.user?.userId;

                      // Check if this buyer is already in the winnerSharedTo array
                      const isAlreadyShared =
                        tableData?.PropertyDetails?.winnerSharedTo?.includes(
                          buyerId
                        );

                      return (
                        <TableRow
                          key={buyerId || index}
                          sx={{
                            borderBottom: '1px solid #ddd',
                            minHeight: '50px',
                            opacity: isAlreadyShared ? 0.5 : 1,
                            filter: isAlreadyShared ? 'blur(0.5px)' : 'none',
                          }}
                        >
                          <TableCell
                            sx={{
                              width: '50px',
                              textAlign: 'center',
                              padding: '8px',
                            }}
                          >
                            <Tooltip
                              title={
                                isAlreadyShared ? 'Offer has been shared' : ''
                              }
                              arrow
                              placement="top"
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    fontFamily: 'Poppins',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                  },
                                },
                              }}
                              PopperProps={{
                                disablePortal: false,
                                sx: {
                                  zIndex: 1500,
                                },
                              }}
                            >
                              <Box sx={{ display: 'inline-block' }}>
                                {isAlreadyShared ? (
                                  <Box
                                    sx={{
                                      width: '16px',
                                      height: '16px',
                                      border: '1px solid #ccc',
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '2px',
                                      cursor: 'not-allowed',
                                      filter: 'blur(0.5px)',
                                    }}
                                  />
                                ) : (
                                  <input
                                    type="checkbox"
                                    checked={selectedBuyers.includes(buyerId)}
                                    onChange={(e) => {
                                      setSelectedBuyers((prev) =>
                                        e.target.checked
                                          ? [...prev, buyerId]
                                          : prev.filter((id) => id !== buyerId)
                                      );
                                    }}
                                    style={{
                                      accentColor: '#A55FFB',
                                      width: '16px',
                                      height: '16px',
                                      cursor: 'pointer',
                                    }}
                                  />
                                )}
                              </Box>
                            </Tooltip>
                          </TableCell>

                          <TableCell
                            sx={{
                              fontFamily: 'Poppins',
                              textAlign: 'left',
                              padding: '22px',
                              filter: isAlreadyShared ? 'blur(0.5px)' : 'none',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              {buyer?.buyerDetails?.user?.userName}{' '}
                              {buyer?.buyerDetails?.user?.userSurName}
                            </Box>
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '16px',
                              fontWeight: 500,
                              fontFamily: 'Poppins',
                              textAlign: 'center',
                              filter: isAlreadyShared ? 'blur(0.5px)' : 'none',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '16px',
                                  fontWeight: 500,
                                  fontFamily: 'Poppins',
                                }}
                              >
                                {buyer?.buyerDetails?.buyerOffer?.offerPrice ||
                                  '-'}
                              </Typography>

                              {buyer?.buyerOfferDetails && (
                                <Typography
                                  sx={{
                                    ...poppins500,
                                    ...fontsize10,
                                    background:
                                      buyer?.buyerOfferDetails
                                        ?.logIsConditional === 1
                                        ? 'rgba(237, 11, 11, 0.6)'
                                        : 'rgba(16, 211, 94, 1)',
                                    borderRadius: '5px',
                                    color: 'white',
                                    padding: '4px 8px',
                                    height: '22px',
                                    minWidth: '90px',
                                    marginTop: '4px',
                                  }}
                                >
                                  {buyer?.buyerOfferDetails
                                    ?.logIsConditional === 1
                                    ? 'Conditional'
                                    : 'Unconditional'}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: '14px',
                              fontWeight: '400',
                              fontFamily: 'Poppins',
                              color: '#112347',
                              textAlign: 'center',
                              padding: '8px',
                              filter: isAlreadyShared ? 'blur(0.5px)' : 'none',
                            }}
                          >
                            {buyer?.buyerDetails?.noOfOffers || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No buyers available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* FOOTER */}
        <Grid
          item
          container
          justifyContent="flex-end"
          gap={matchessm ? '5px' : 2}
          marginTop={matchessm ? '15px' : 5}
          alignItems="center"
        >
          <Button
            variant="outlined"
            disabled={loading}
            sx={{
              padding: matchessm ? '6px 23px' : '10px 39px',
              height: matchessm ? '35px' : '41px',
              fontSize: matchessm ? '12px' : '15px',
              color: '#666666',
              fontWeight: '400',
              border: '1px solid rgba(17, 35, 71, 0.1)',
              borderRadius: '5px',
              textTransform: 'none',
              '&.Mui-disabled': {
                // backgroundColor: '#4CA4D6',
                // color: '#fff',
                // opacity: 0.7,
                cursor: 'not-allowed',
                pointerEvents: 'auto',
              },
            }}
            onClick={onClose}
          >
            Cancel
          </Button>

          <Box display="flex" alignItems="center" gap={2}>
            <Button
              disabled={loading || newlySelectedEligibleBuyers.length < 1}
              sx={{
                padding: matchessm ? '6px 30px' : '10px 50px',
                height: matchessm ? '35px' : '41px',
                fontSize: matchessm ? '12px' : '15px',
                color: '#FFFFFF',
                fontWeight: '400',
                backgroundColor: '#4CA4D6',
                borderRadius: '5px',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#4CA4D6' },
                '&.Mui-disabled': {
                  backgroundColor: '#4CA4D6',
                  color: '#fff',
                  opacity: 0.7,
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                },
              }}
              onClick={handleShare}
            >
              Share
            </Button>

            {loading && (
              <img
                alt="Loading"
                src={Loading}
                className="loaderImage"
                style={{ height: '40px', width: '40px' }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default ShareWinnerDialog;
